import { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { SettingsDropdown } from "./SettingsDropdown";
import { useAuth } from "../hooks/useAuth";
import "./Header.css";

const NAV = [
  { to: "/services", label: "Services & Products" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();

  async function handleLogout() {
    await logout();
    navigate("/");
  }

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  return (
    <header className={`header ${scrolled ? "is-scrolled" : ""}`} data-drawer-open={drawerOpen}>
      <div className="container header__inner">
        <Link to="/" className="header__brand" aria-label="SilicoInformatics home">
          <img
            src="/silico-logo.png"
            alt=""
            className="header__logo-mark"
            width="36"
            height="36"
            draggable={false}
          />
          <span className="header__brand-name">SilicoInformatics</span>
        </Link>

        <nav className="header__nav" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) => `header__nav-link ${isActive ? "is-active" : ""}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="header__actions">
          <SettingsDropdown />
          {/* Suppress the auth area entirely during initial refresh — avoids
              a "Get started" → user-avatar flicker on returning visitors. */}
          {loading ? null : user ? (
            <>
              <Link to="/dashboard" className="header__user-link" title={user.email}>
                <span className="header__user-avatar" aria-hidden>
                  {(user.firstName?.[0] ?? "?").toUpperCase()}
                </span>
                <span className="header__user-name">{user.firstName}</span>
              </Link>
              <button
                type="button"
                className="btn btn--ghost header__cta"
                onClick={handleLogout}
              >
                Log out
              </button>
            </>
          ) : (
            <Link to="/login" className="btn btn--primary header__cta">
              Get started
            </Link>
          )}
          <button
            type="button"
            className="header__menu-btn"
            aria-label={drawerOpen ? "Close menu" : "Open menu"}
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen((d) => !d)}
          >
            <span /><span /><span />
          </button>
        </div>
      </div>

      <div className={`header__drawer ${drawerOpen ? "is-open" : ""}`} role="dialog" aria-modal="true">
        <nav className="header__drawer-nav" aria-label="Mobile">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className="header__drawer-link">
              {n.label}
            </NavLink>
          ))}
          {user ? (
            <>
              <Link to="/dashboard" className="header__drawer-link">Dashboard</Link>
              <button type="button" className="btn btn--ghost" onClick={handleLogout}>
                Log out
              </button>
            </>
          ) : (
            <>
              <Link to="/signup" className="header__drawer-link">Create account</Link>
              <Link to="/login" className="btn btn--primary">Get started</Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
